import React from 'react'
import styled, { css } from 'styled-components'
import { BaseWrapper, H4, Link } from '@base'
import { Devices, Spacings, Text } from '@styles'
import Invoiced from '@assets/dashboard/invoiced.svg'
import ProgressBar from '../ProgressBar'
import { displayAmount, getTodayDate } from '@utils'
import { LocationSearching, StarRate } from '@material-ui/icons'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { MB_ROLES, strings } from '@constants'
import { paths } from '@routes'
import { HoverDetails, ItemIconWrapper } from './EnquiryPaymentOrder'

const Wapper = styled(BaseWrapper)`
	display: flex;
	width: 100%;
	padding: ${Spacings.SPACING_4B};
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: ${Spacings.SPACING_4B};

	${({ FlexColumn }) => {
		if (FlexColumn) {
			return css`
				flex-direction: column;
			`
		}
	}}
`
const CardContainer = styled.div`
	display: flex;
	width: 100%;
	flex-direction: column;
	border-radius: ${Spacings.SPACING_2B};
	border: 1px solid rgba(234, 241, 250, 1);
	transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
	border-color: ${({ theme }) => theme.palette.background.disabled};
`

const CardContainerWapper = styled.div`
	display: flex;
	padding: 0.875rem ${Spacings.SPACING_4B};
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	gap: ${Spacings.SPACING_6B};
	align-self: stretch;
`

const TextContainer = styled.div`
	display: flex;
	justify-content: space-between;
	align-self: stretch;
	flex-direction: column;
	align-items: flex-start;
	@media ${Devices.mobile} {
		flex-direction: row;
		align-items: center;
	}
`

const TextImageContainer = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_4B};
`
const ImageWapper = styled.div`
	display: flex;
	padding: ${Spacings.SPACING_2B};
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	border-radius: 0.2375rem;
	border: 0.95px solid var(--Cool-Gray-20, ${({ theme }) => theme.palette.text.greyFive});
`

const TextWapper = styled(H4)`
	overflow: hidden;
	color: var(--Cool-Gray-60, ${({ theme }) => theme.palette.text.greyThree});
	text-overflow: ellipsis;

	font-family: Poppins;
	font-size: ${Text.LARGE};
	font-style: normal;
	font-weight: ${Text.BOLD_500};
	line-height: ${Spacings.SPACING_7B};
`
const TextWapperTwo = styled(H4)`
	color: var(--Cool-Gray-100, ${({ theme }) => theme.palette.text.greySix});
	font-family: Poppins;
	font-size: ${({ SmallText }) => (SmallText ? Spacings.SPACING_6B : Spacings.SPACING_8B)};
	font-style: normal;
	font-weight: ${Text.BOLD_500};
	line-height: ${Spacings.SPACING_10B};

	span {
		color: var(--Cool-Gray-50, ${({ theme }) => theme.palette.text.greyTwo});
		font-family: Poppins;
		font-size: ${Text.MEDIUM};
		font-style: normal;
		font-weight: ${Text.BOLD_500};
		line-height: ${Spacings.SPACING_4B};
	}
`
const ProgressBarWapper = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	align-items: center;
`
const GrossWaaper = styled.div`
	display: flex;
	margin: ${Spacings.SPACING_4B};
`
const InvoicedTargetHoverWapper = styled.div`
	display: flex;
	align-items: center;
`

function buildLedgerUrl(base, dynamicParams) {
	const query = Object.entries(dynamicParams)
		.filter(([, v]) => v !== null && v !== undefined && v !== '')
		.map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
		.join('&')
	return query ? `${base}&${query}` : base
}

const InvoicedTarget = ({
	dashboardType,
	hasOrganisationId,
	total_sales_business_amount,
	credit_amount,
	monthly_target,
	gross_margin,
	yesterdays_invoice_amount,
	organisationName,
	employeeName,
	fromDate,
	toDate,
}) => {
	const dynamicParams = {
		organisation: organisationName,
		manager_name: employeeName === 'ALL' ? '' : employeeName,
		from: fromDate,
		to: toDate,
	}
	const servedPercent = Math.round((total_sales_business_amount / monthly_target) * 100)
	const isSalesAdmin = dashboardType === MB_ROLES.SALES

	const invoicedHoverItems = [
		{ key: 'Total Invoiced', value: `${displayAmount(total_sales_business_amount)} Cr` },
		...(!hasOrganisationId ? [{ key: 'Credit Notes', value: `${displayAmount(credit_amount)} Cr` }] : []),
	]

	return (
		<Wapper FlexColumn={true}>
			<CardContainer>
				<CardContainerWapper>
					<TextContainer>
						<TextImageContainer>
							<ImageWapper>
								<img src={Invoiced} alt='invoiced' />
							</ImageWapper>
							<InvoicedTargetHoverWapper>
								<TextWapper>{hasOrganisationId ? 'Invoiced' : 'Invoiced vs Target'}</TextWapper>
								{!!invoicedHoverItems.length && (
									<ItemIconWrapper>
										<InfoOutlinedIcon fontSize='small' />
										<HoverDetails hoverItems={invoicedHoverItems} />
									</ItemIconWrapper>
								)}
							</InvoicedTargetHoverWapper>
						</TextImageContainer>
						<Link
							to={buildLedgerUrl(`${paths.admin_ledger}?transaction_source=INVOICE&transaction_type=DEBIT`, dynamicParams)}
							rel='noopener noreferrer'
							target='_blank'
						>
							<TextWapperTwo SmallText>
								<>
									{displayAmount(isSalesAdmin ? total_sales_business_amount - credit_amount : total_sales_business_amount)}
									{!hasOrganisationId && <> vs {displayAmount(monthly_target)} </>}Cr
								</>
							</TextWapperTwo>
						</Link>
					</TextContainer>
					{!isSalesAdmin && (
						<TextContainer>
							<TextImageContainer>
								<ImageWapper>
									<img src={Invoiced} alt='invoiced' />
								</ImageWapper>
								<TextWapper>{strings('credit', 'notes')}</TextWapper>
							</TextImageContainer>
							<Link
								to={buildLedgerUrl(`${paths.admin_ledger}?transaction_source=CREDIT_NOTE&transaction_type=CREDIT`, dynamicParams)}
								rel='noopener noreferrer'
								target='_blank'
							>
								<TextWapperTwo SmallText>{displayAmount(credit_amount)} Cr</TextWapperTwo>
							</Link>
						</TextContainer>
					)}

					<ProgressBarWapper>
						<ProgressBar fillColor={servedPercent} value={servedPercent} label={'Invoiced'} fontSize={Text.EXTRA_SMALL} />
					</ProgressBarWapper>
				</CardContainerWapper>
			</CardContainer>
			<CardContainer>
				<GrossWaaper>
					<ProgressBarWapper>
						<TextImageContainer>
							<ImageWapper>
								<LocationSearching fontSize='small' />
							</ImageWapper>
							<TextWapper>Gross Margin</TextWapper>
						</TextImageContainer>
						<TextWapperTwo SmallText>{gross_margin > 0 ? <span> {displayAmount(gross_margin)} Cr</span> : '-'}</TextWapperTwo>
					</ProgressBarWapper>
				</GrossWaaper>
			</CardContainer>
			<CardContainer>
				<GrossWaaper>
					<ProgressBarWapper>
						<TextImageContainer>
							<ImageWapper>
								<StarRate fontSize='small' />
							</ImageWapper>
							<TextWapper>Invoiced yesterday</TextWapper>
						</TextImageContainer>
						<Link
							to={`${paths.admin_ledger}?transaction_source=INVOICE&transaction_type=DEBIT&from=${
								getTodayDate().getTime() - 86400000
							}&to=${getTodayDate(true).getTime() - 86400000}`}
							rel='noopener noreferrer'
							target='_blank'
						>
							<TextWapperTwo SmallText>
								{yesterdays_invoice_amount > 0 ? <span> {displayAmount(yesterdays_invoice_amount)} Cr</span> : '-'}
							</TextWapperTwo>
						</Link>
					</ProgressBarWapper>
				</GrossWaaper>
			</CardContainer>
		</Wapper>
	)
}

export default InvoicedTarget
